import { useCallback, useState } from 'react';

import { EntityWithId } from '../types/types';

enum ACTION_TYPES {
  SELECTION_CHANGE = 'SELECTION_CHANGE',
  RESET_SELECTION = 'RESET_SELECTION',
  SET_SELECTION = 'SET_SELECTION',
}

interface selectionChangeAction<T> {
  type: ACTION_TYPES.SELECTION_CHANGE;
  payload: SelectionChangeParams<T>;
}

interface resetSelectionAction {
  type: ACTION_TYPES.RESET_SELECTION;
}

interface setSelectionAction<T> {
  type: ACTION_TYPES.SET_SELECTION;
  payload: UpdateSelectionParams<T>;
}

type Action<T> =
  | selectionChangeAction<T>
  | resetSelectionAction
  | setSelectionAction<T>;

interface SelectionChangeParams<T = EntityWithId> {
  entity: T;
  checked?: boolean;
}

interface UpdateSelectionParams<T = EntityWithId> {
  selectedNodes: T[];
  keepExisting: boolean;
}

const entitySelectionReducer = <T extends EntityWithId>(
  state: T[],
  action: Action<T>,
) => {
  switch (action.type) {
    case ACTION_TYPES.SELECTION_CHANGE:
      const changePayload = action.payload;
      return changePayload.checked
        ? [...state, changePayload.entity]
        : state.filter((e) => e.id !== changePayload.entity.id);
    case ACTION_TYPES.RESET_SELECTION:
      return [];
    case ACTION_TYPES.SET_SELECTION:
      const selectionPayload = action.payload;
      return selectionPayload.keepExisting
        ? [...state, ...selectionPayload.selectedNodes]
        : selectionPayload.selectedNodes;
    default:
      throw new Error();
  }
};

/**
 * this hook manages state changes for entity selection
 * the state can be stored in the hook or somewhere else using setSelectedEntities
 * multiple: boolean - whether multiple entities can be selected
 * selectedEntities?: Array<Entity> - optional, makes the state controlled instead of storing it in the hook
 * setSelectedEntities?: (Array<Entity>) => void - optional, makes the state controlled instead of storing it in the hook
 */
interface UseEntitySelectionProps<T extends EntityWithId> {
  multiple: boolean;
  selectedEntities?: T[];
  setSelectedEntities?: (entities: T[]) => void;
}

interface UseEntitySelectionReturnType<T extends EntityWithId> {
  selectedEntities: T[];
  onSelectionChange: (props: SelectionChangeParams<T>) => void | undefined;
  setSelection: (
    selectedNodes: T[],
    keepExisting?: boolean,
  ) => void | undefined;
  clearSelection: () => void | undefined;
}

export default <T extends EntityWithId>({
  multiple,
  selectedEntities: selectedEntitiesProp,
  setSelectedEntities: setSelectedEntitiesProp,
}: UseEntitySelectionProps<T>): UseEntitySelectionReturnType<T> => {
  const isControlled = selectedEntitiesProp && setSelectedEntitiesProp;
  const [uncontrolledSelectedEntities, setUncontrolledSelectedEntities] =
    useState<T[]>([]);
  const controlledDispatch = useCallback(
    (action: Action<T>) =>
      setSelectedEntitiesProp?.(
        entitySelectionReducer(selectedEntitiesProp || [], action),
      ),
    [selectedEntitiesProp, setSelectedEntitiesProp],
  );
  const uncontrolledDispatch = useCallback((action: Action<T>) => {
    setUncontrolledSelectedEntities((prevState) =>
      entitySelectionReducer(prevState, action),
    );
  }, []);
  const dispatch = isControlled ? controlledDispatch : uncontrolledDispatch;

  const onSelectionChange = useCallback(
    ({ entity, checked }: SelectionChangeParams<T>) =>
      dispatch(
        multiple
          ? {
              type: ACTION_TYPES.SELECTION_CHANGE,
              payload: { entity, checked },
            }
          : {
              type: ACTION_TYPES.SET_SELECTION,
              payload: {
                selectedNodes: checked ? [entity] : [],
                keepExisting: false,
              },
            },
      ),
    [dispatch, multiple],
  );

  const setSelection = useCallback(
    (selectedNodes: T[], keepExisting?: boolean) =>
      dispatch({
        type: ACTION_TYPES.SET_SELECTION,
        payload: { selectedNodes, keepExisting: !!keepExisting },
      }),
    [dispatch],
  );
  const clearSelection = useCallback(
    () => dispatch({ type: ACTION_TYPES.RESET_SELECTION }),
    [dispatch],
  );

  return {
    selectedEntities: isControlled
      ? selectedEntitiesProp
      : uncontrolledSelectedEntities,
    onSelectionChange,
    setSelection,
    clearSelection,
  };
};
